import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { connect } from "react-redux";
import styled from "styled-components";

const enum fields {
    image = "image",
    imageEyebrow = "imageEyebrow",
    imageCaption = "imageCaption",
    imageButtonText = "imageButtonText",
    imageButtonVariant = "imageButtonVariant",
    hideImageButton = "hideImageButton",
    imageButtonLink = "imageButtonLink",
    imageContentBackgroundColor = "imageContentBackgroundColor",
    panelEyebrow = "panelEyebrow",
    heading = "heading",
    description = "description",
    contactButtonText = "contactButtonText",
    contactButtonVariant = "contactButtonVariant",
    hideContactButton = "hideContactButton",
    contactButtonLink = "contactButtonLink",
    accentColor = "accentColor",
    imageOverlayColor = "imageOverlayColor",
    panelBackgroundColor = "panelBackgroundColor",
    textColor = "textColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.imageEyebrow]: string;
        [fields.imageCaption]: string;
        [fields.imageButtonText]: string;
        [fields.imageButtonVariant]: "primary" | "secondary" | "tertiary";
        [fields.hideImageButton]: boolean;
        [fields.imageButtonLink]: LinkFieldValue;
        [fields.imageContentBackgroundColor]: string;
        [fields.panelEyebrow]: string;
        [fields.heading]: string;
        [fields.description]: string;
        [fields.contactButtonText]: string;
        [fields.contactButtonVariant]: "primary" | "secondary" | "tertiary";
        [fields.hideContactButton]: boolean;
        [fields.contactButtonLink]: LinkFieldValue;
        [fields.accentColor]: string;
        [fields.imageOverlayColor]: string;
        [fields.panelBackgroundColor]: string;
        [fields.textColor]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    imageButtonUrl: ownProps.fields.imageButtonLink ? getLink(state, ownProps.fields.imageButtonLink)?.url : undefined,
    contactButtonUrl: ownProps.fields.contactButtonLink
        ? getLink(state, ownProps.fields.contactButtonLink)?.url
        : undefined,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const onButtonClick = (history: History, url?: string) => {
    if (url) {
        history.push(url);
    }
};

type PartFinderFeatureProps = Props & HasHistory;

const PartFinderFeature: React.FC<PartFinderFeatureProps> = ({
    fields: content,
    imageButtonUrl,
    contactButtonUrl,
    history,
}) => {
    return (
        <Section>
            <ImagePanel imageOverlayColor={content.imageOverlayColor}>
                <FeatureImage src={content.image} alt="" />
                <ImageContent backgroundColor={content.imageContentBackgroundColor || "#00000024"}>
                    <ImageCopy>
                        <ImageEyebrow accentColor={content.accentColor}>{content.imageEyebrow}</ImageEyebrow>
                        <ImageCaption>{content.imageCaption}</ImageCaption>
                    </ImageCopy>
                    {!content.hideImageButton && (
                        <FeatureButton
                            variant={content.imageButtonVariant || "primary"}
                            onClick={() => onButtonClick(history, imageButtonUrl)}
                        >
                            {content.imageButtonText}
                        </FeatureButton>
                    )}
                </ImageContent>
            </ImagePanel>
            <MessagePanel backgroundColor={content.panelBackgroundColor} textColor={content.textColor}>
                <StyledWrapper>
                    <PanelEyebrow accentColor={content.accentColor}>{content.panelEyebrow}</PanelEyebrow>
                    <Heading>{content.heading}</Heading>
                </StyledWrapper>
                <StyledWrapper>
                    <Description>{content.description}</Description>
                    {!content.hideContactButton && (
                        <MessageButton
                            variant={content.contactButtonVariant || "secondary"}
                            onClick={() => onButtonClick(history, contactButtonUrl)}
                        >
                            {content.contactButtonText}
                        </MessageButton>
                    )}
                </StyledWrapper>
            </MessagePanel>
        </Section>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.accentColor,
            displayName: "Accent Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#ef3d45",
            fieldType: "General",
        },
        {
            name: fields.imageOverlayColor,
            displayName: "Image Overlay Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#000000",
            fieldType: "General",
        },
        {
            name: fields.panelBackgroundColor,
            displayName: "Message Panel Background",
            editorTemplate: "ColorPickerField",
            defaultValue: "#000000",
            fieldType: "General",
        },
        {
            name: fields.textColor,
            displayName: "Panel Text Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#ffffff",
            fieldType: "General",
        },
        {
            name: fields.imageContentBackgroundColor,
            displayName: "Image Content Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#00000024",
            fieldType: "General",
        },
        {
            name: fields.image,
            displayName: "Feature Image",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.imageEyebrow,
            displayName: "Image Eyebrow",
            editorTemplate: "TextField",
            defaultValue: "ON THE FLOOR",
            fieldType: "General",
        },
        {
            name: fields.imageCaption,
            displayName: "Image Caption",
            editorTemplate: "TextField",
            defaultValue: "45,000+ PARTS. PULLED AND SHIPPED SAME DAY.",
            fieldType: "General",
        },
        {
            name: fields.imageButtonText,
            displayName: "Image Button Text",
            editorTemplate: "TextField",
            defaultValue: "Shop Now",
            fieldType: "General",
        },
        {
            name: fields.imageButtonVariant,
            displayName: "Image Button Variant",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: "primary" },
                { displayName: "Secondary", value: "secondary" },
                { displayName: "Tertiary", value: "tertiary" },
            ],
            defaultValue: "primary",
            fieldType: "General",
        },
        {
            name: fields.hideImageButton,
            displayName: "Hide Image Button",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
        {
            name: fields.imageButtonLink,
            displayName: "Image Button Link",
            editorTemplate: "LinkField",
            defaultValue: {
                value: "#",
                type: "Url",
            },
            fieldType: "General",
        },
        {
            name: fields.panelEyebrow,
            displayName: "Panel Eyebrow",
            editorTemplate: "TextField",
            defaultValue: "HAVE A PART TO FIND?",
            fieldType: "General",
        },
        {
            name: fields.heading,
            displayName: "Heading",
            editorTemplate: "TextField",
            defaultValue: "A NUMBER, A PICTURE, A PROBLEM, OR AN IDEA - BRING IT.",
            fieldType: "General",
        },
        {
            name: fields.description,
            displayName: "Description",
            editorTemplate: "TextField",
            defaultValue: "Our reps will track down the right fit and get it to you, on time, at a fair price.",
            fieldType: "General",
        },
        {
            name: fields.contactButtonText,
            displayName: "Contact Button Text",
            editorTemplate: "TextField",
            defaultValue: "CONTACT A REP",
            fieldType: "General",
        },
        {
            name: fields.contactButtonVariant,
            displayName: "Contact Button Variant",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary", value: "primary" },
                { displayName: "Secondary", value: "secondary" },
                { displayName: "Tertiary", value: "tertiary" },
            ],
            defaultValue: "secondary",
            fieldType: "General",
        },
        {
            name: fields.hideContactButton,
            displayName: "Hide Contact Button",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
        {
            name: fields.contactButtonLink,
            displayName: "Contact Button Link",
            editorTemplate: "LinkField",
            defaultValue: {
                value: "/contact-us",
                type: "Url",
            },
            fieldType: "General",
        }
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(PartFinderFeature)),
    definition,
};

export default widgetModule;

const Section = styled.section`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: stretch;
    grid-auto-rows: minmax(240px, 1fr);
    gap: clamp(16px, 2vw, 24px);
    width: 100%;
    padding: 4px;
    box-sizing: border-box;
    font-family: Arial, sans-serif;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

const ImagePanel = styled.div<{ imageOverlayColor: string }>`
    position: relative;
    min-width: 0;
    min-height: 240px;
    overflow: hidden;
    background: #222;

    &::after {
        position: absolute;
        inset: 0;
        content: "";
        background: linear-gradient(
            to top,
            ${({ imageOverlayColor }) => imageOverlayColor || "#000"}cc 0%,
            ${({ imageOverlayColor }) => imageOverlayColor || "#000"}55 25%,
            transparent 58%
        );
        pointer-events: none;
    }
`;

const FeatureImage = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    min-height: 240px;
    aspect-ratio: 1.75;
    object-fit: cover;
`;

const ImageContent = styled.div<{ backgroundColor: string }>`
    position: absolute;
    right: 0px;
    bottom: 0px;
    left: 0px;
    z-index: 1;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    padding: 15px;
    background: ${({ backgroundColor }) => backgroundColor};
`;

const ImageCopy = styled.div`
    min-width: 0;
    color: #fff;
`;

const ImageEyebrow = styled.div<{ accentColor: string }>`
    margin-bottom: 7px;
    color: ${({ accentColor }) => accentColor || "#ef3d45"};
    font-size: clamp(10px, 1.1vw, 13px);
    font-weight: 700;
    line-height: 1.1;
    overflow-wrap: anywhere;
`;

const ImageCaption = styled.div`
    font-size: clamp(11px, 1.3vw, 15px);
    font-weight: 700;
    line-height: 1.2;
    overflow-wrap: anywhere;
`;

const MessagePanel = styled.div<{ backgroundColor: string; textColor: string }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    min-width: 0;
    min-height: 240px;
    padding: clamp(26px, 4vw, 48px) clamp(22px, 3vw, 30px);
    box-sizing: border-box;
    background: ${({ backgroundColor }) => backgroundColor || "#000"};
    color: ${({ textColor }) => textColor || "#fff"};
    padding-bottom: 15px;
`;

const PanelEyebrow = styled.div<{ accentColor: string }>`
    margin-bottom: clamp(22px, 3vw, 20px);
    color: ${({ accentColor }) => accentColor || "#ef3d45"};
    font-size: clamp(14px, 1.2vw, 14px);
    font-weight: 700;
    line-height: 1.1;
`;

const Heading = styled.h2`
    max-width: 650px;
    margin: 0;
    font-size: clamp(28px, 2.6vw, 48px);
    font-weight: 800;
    line-height: 0.98;
    letter-spacing: 0;
    text-transform: uppercase;
    overflow-wrap: anywhere;
`;

const Description = styled.p`
    max-width: 680px;
    margin: clamp(28px, 3.5vw, 30px) 0 0;
    font-size: clamp(11px, 1.2vw, 15px);
    line-height: 1.35;
    overflow-wrap: anywhere;
    margin-bottom: 15px;
`;

const FeatureButton = styled(Button)`
    flex: 0 0 auto;
    white-space: nowrap;
`;

const MessageButton = styled(FeatureButton)`
    margin-top: auto;
`;
